<script setup lang="ts">
import { mdiMenuDown, mdiMenuUp } from "@mdi/js";
import Icon from "~/components/ui/Icon.vue";
import { useBetbook } from "~/store/betbook";
import EventPrice from "./EventPrice.vue";
import { IOutcome } from "~/types/sports/outcome";
import { IMarket } from "~/types/sports/market";
import t from "~/utils/translate";
import { IEvent } from "~/types/sports/event";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import { totalGoalMarkets } from "~/utils/constants";
import { useBetslip } from "~/store/betslip";

interface Props {
	active: string;
	event: IEvent | undefined;
}

const props = defineProps<Props>();
const store = useBetbook();
const betslip = useBetslip();
const emits = defineEmits(["set-active"]);
const open = ref(false);
const router = useRouter();
const route = useRoute();
const { fixtureType } = route.params;

const container = ref<HTMLElement>();
const findOutcomesByMarketName = (marketName: string): { market: IMarket | undefined; outcomes: string[] | undefined } => {
	try {
		const market = [...store.markets.values()].filter((x) => x.name === marketName && props.event && x.eventId === props.event.eventId)[0];
		const outcomes = store.pointers.outcomes.get(market.marketId);
		return { market, outcomes };
	} catch (e) {
		return {
			market: undefined,
			outcomes: undefined,
		};
	}
};

const orderedMarkets = computed(() => {
	try {
		const activeMarket = Object.values(totalGoalMarkets).filter((x) => x.name === props.active)[0];
		return [...new Set([activeMarket, ...Object.values(totalGoalMarkets)])];
	} catch (e) {
		return Object.values(totalGoalMarkets);
	}
});

function handleClickOutside(event: MouseEvent) {
	if (!container?.value?.contains(event.target as Node)) {
		open.value = false;
		document.removeEventListener("click", handleClickOutside);
	}
}

const isDisabled = ({ market, outcomes }: { market: IMarket | undefined; outcomes: string[] | undefined }) => {
	return (
		!market ||
		market.isSuspended ||
		!market.shouldDisplay ||
		!outcomes?.length ||
		(props.event && !store.isEventActive(props.event)) ||
		!props.event?.shouldDisplay
	);
};

watchEffect(() => {
	if (open.value) {
		setTimeout(() => {
			document.addEventListener("click", handleClickOutside);
			// container.value?.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 150);
	}
});

onUnmounted(() => {
	document.removeEventListener("click", handleClickOutside);
});

const handleMoreMarketsRoute = () => {
	router.push(
		sportUrlBuilder({
			fixtureType: fixtureType,
			sport: props.event?.sportId,
			event: {
				awayTeam: props.event?.awayTeam ?? "",
				homeTeam: props.event?.homeTeam ?? "",
				eventId: props.event?.eventId ?? 0,
				leagueId: props.event?.leagueId ?? "",
				regionId: props.event?.regionId ?? "",
				expectedEndEpoch: props.event?.expectedEndEpoch ?? 0,
				expectedStartEpoch: props.event?.expectedStartEpoch ?? 0,
			},
		})
	);
};
</script>

<template>
	<div
		@click="open = !open"
		class="relative flex items-center justify-center flex-1 h-[52px] text-xs font-bold border rounded-md cursor-pointer md:h-8 bg-light-200 dark:bg-dark-700 border-light-500 dark:border-dark-400"
	>
		<div>{{ totalGoalMarkets[active] ? totalGoalMarkets[active].displayName : "" }}</div>
		<div class="flex items-center justify-center h-full -mr-3">
			<Icon class="w-5 h-5 right-1 fill-dark-800 dark:fill-light-50" :path="mdiMenuDown" />
		</div>
	</div>
	<div
		ref="container"
		v-if="open"
		@click="open = false"
		class="absolute top-0 left-0 right-0 z-[8] min-w-[65px] flex flex-col gap-1 p-1 border rounded-md bg-light-200 dark:bg-dark-700 border-light-500 dark:border-dark-400 shadow"
	>
		<div
			v-for="(item, index) in orderedMarkets"
			:key="item.name"
			class="flex items-center gap-1 font-bold cursor-pointer h-9"
			@click="
				(e) => {
					if (active !== item.name) emits('set-active', item.name);
				}
			"
		>
			<div class="flex items-center flex-1 gap-1">
				<div :class="active === item.name && 'text-identity'" class="flex items-center justify-center w-9 hover:text-identity">
					{{ item.displayName }}
					<div class="flex items-center justify-center h-full -mr-2" v-if="index === 0">
						<Icon class="w-5 h-5 right-1 fill-dark-800 dark:fill-light-50" :path="mdiMenuUp" />
					</div>
				</div>
				<div class="flex flex-1 gap-1" v-if="findOutcomesByMarketName(item.name).market && !isDisabled(findOutcomesByMarketName(item.name))">
					<EventPrice
						hideIndicators
						small
						outcomeClass="h-8"
						v-for="outcome in findOutcomesByMarketName(item.name).outcomes"
						@click="
							(e) => {
								if (!betslip.isSelectionActive(outcome)) e.stopPropagation();
							}
						"
						:key="outcome"
						:price="store.prices.get(outcome)"
						:outcome="store.outcomes.get(outcome) as IOutcome"
						:eventId="findOutcomesByMarketName(item.name).market?.eventId as number"
						:market="findOutcomesByMarketName(item.name).market"
					/>
				</div>
				<div
					@click="handleMoreMarketsRoute"
					v-else
					class="flex items-center justify-center flex-1 h-8 font-normal border rounded-md border-identity-100 dark:border-dark-400"
					:class="findOutcomesByMarketName(item.name).market?.isSuspended ? 'bg-light-500 dark:bg-dark-400' : 'bg-identity-50 dark:bg-dark-800'"
				>
					<span v-if="findOutcomesByMarketName(item.name).market?.isSuspended">{{ t("suspended") }}</span>
					<span v-else>{{ t("more-bets") }}</span>
				</div>
			</div>
		</div>
	</div>
</template>
